/**
 * Column contract shared by every Supplier Performance drill-down.
 *
 * One column definition drives both the on-screen table (DrilldownTable) and
 * the Excel export (exportDrilldown), so "the export has the same columns as
 * the table" holds by construction rather than by keeping two lists in step.
 */
import { ReactNode } from 'react';

export interface DrilldownFormatters {
  formatDate: (d: string | Date) => string;
  formatDateTime: (d: string | Date) => string;
  /** Full-precision money: symbol, thousands separators, 2 dp ("£1,234.56"). */
  money: (value: number, currency?: string) => string;
  /** Fallback currency for a money column whose row carries none. */
  currency: string;
}

export type CellValue = string | number | null | undefined;

/** Loosely-typed row for the modal, which receives `unknown[]`. */
export type AnyRow = Record<string, unknown>;

export interface DrilldownColumn<T> {
  key: string;
  label: string;
  /** Header tooltip: what the column means, where its definition is not obvious. */
  title?: string;
  align?: 'left' | 'right' | 'center';
  /** 'qty' → thousands separators, at most 2 dp; 'money' → currency(row). */
  kind?: 'text' | 'qty' | 'money';
  /** The cell's value. Numbers stay numbers so Excel can sum them. */
  value: (row: T) => CellValue;
  /** Currency for a money column; falls back to the page currency. */
  currency?: (row: T) => string | null | undefined;
  /** Richer on-screen cell (links, badges). The export always uses value(). */
  render?: (row: T) => ReactNode;
  /** Long free text: clamp the cell and put the full text in its tooltip. */
  truncate?: boolean;
  /** Extra classes for the <td>. */
  className?: string;
}

/** Parse a serializer decimal string ("6.260") to a number; null when absent. */
export function num(v: CellValue): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Quantity: thousands separators, at most 2 dp (6.260 → 6.26; 1257750 → 1,257,750). */
export function fmtQty(v: CellValue): string {
  const n = num(v);
  return n === null ? '-' : n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

/** A formatted date, or null when the row has none. */
export function dateOf(fmt: (d: string | Date) => string, v: string | null | undefined): string | null {
  return v ? fmt(v) : null;
}

/** Yes / No for a boolean cell. */
export const yesNo = (v: boolean | null | undefined): string => (v ? 'Yes' : 'No');

/** The text the table shows for a column's value ('-' when absent). */
export function displayValue<T>(column: DrilldownColumn<T>, row: T, f: DrilldownFormatters): string {
  const v = column.value(row);
  if (v === null || v === undefined || v === '') return '-';
  if (column.kind === 'qty') return fmtQty(v);
  if (column.kind === 'money') {
    const n = num(v);
    return n === null ? '-' : f.money(n, column.currency?.(row) || f.currency);
  }
  return String(v);
}

/** The value written to the Excel cell: numbers for qty/money, text otherwise. */
export function exportValue<T>(column: DrilldownColumn<T>, row: T): string | number {
  const v = column.value(row);
  if (v === null || v === undefined) return '';
  if (column.kind === 'qty' || column.kind === 'money') {
    const n = num(v);
    return n === null ? '' : n;
  }
  return v;
}
